import React, { useEffect, useState, useMemo } from "react";
import { Card, Table, Tabs, Button, Input, Alert, Spin, message, Progress, Tag } from "antd";
import { CheckCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import axiosInstance from "../../../utils/axiosInstance";
import { LOCALE_LIST } from "../../../constants/LocaleList";
import { DMA_LIST } from "../../../constants/DMAList";
import { downloadBlob, fetchDesignResult } from "../../rct-experiment/components/rctExperimentApi";

const { TabPane } = Tabs;

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google Ads",
  meta: "Meta Ads",
  universal_ads: "Universal Ads",
};

interface StepApplyBidProps {
  jobConfigId: string;
  onNext: () => void;
  onPrevious: () => void;
}

interface TreatmentMarketRow {
  key: string; // lowercased/trimmed market name, used for matching
  market: string; // original-cased display value
  bidMultiplier: number;
  coverage: number;
  segmentIndices: number[]; // indices into bidsJson["bid-multiplier"] belonging to this market
}

// Causal Loop (RCT)'s Apply Bid step, per the user's explicit ask: unlike
// causal-loop/components/StepApplyBid.tsx (which forks Bid Optimizer's full,
// filterable per-segment ResultStep table), this shows ONE row per Treatment
// market - the markets the RCT 50/50 design (Step 3) actually assigned to
// Treatment - each with a bid multiplier and coverage rolled up from Bid
// Optimizer's per-segment output, plus a CSV/XLSX export of that table. Below
// it, the same Google/Meta/Universal Ads apply-bid tabs as the Synthetic
// Control variant (DB-only persistence via the existing, job-type-agnostic
// /api/causal-loop/{jobConfigId}/apply-bids endpoint).
const StepApplyBid: React.FC<StepApplyBidProps> = ({ jobConfigId, onNext, onPrevious }) => {
  const [bidsJson, setBidsJson] = useState<any>();
  const [jobConfigData, setJobConfigData] = useState<any>();
  const [treatmentLocations, setTreatmentLocations] = useState<Set<string> | null>(null);
  const [appliedBids, setAppliedBids] = useState<any[]>([]);
  const [isAlert, setAlert] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("facebook");
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [universalLoading, setUniversalLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rctUnavailable, setRctUnavailable] = useState(false);
  const [selectedMarketKeys, setSelectedMarketKeys] = useState<React.Key[]>([]);
  const [filterState, setFilterState] = useState<Record<string, any>>({});
  const [tableKey, setTableKey] = useState(0);
  const [formData, setFormData] = useState({
    testAdsetId: "",
    testBudget: "",
    googleTestCampaignId: "",
    googleTestBudget: "",
    universalTestCampaignId: "",
    universalTestBudget: "",
  });

  const regionColumn: string | undefined =
    jobConfigData?.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping?.region_varialbes?.[0];

  // One row per Treatment market: bid multiplier is a coverage-weighted
  // average across that market's Bid Optimizer segments (age/gender/device
  // combinations), coverage is their Coverage %s summed - Coverage is
  // additive by construction (each segment's share of total raw sales across
  // the whole dataset, see bid_multiplier.py), so a market's total coverage
  // is just the sum of its segments' shares.
  const treatmentMarketRows: TreatmentMarketRow[] = useMemo(() => {
    if (!bidsJson?.["bid-multiplier"] || !regionColumn || !treatmentLocations) return [];
    const groups = new Map<string, { display: string; bidWeighted: number; coverageSum: number; indices: number[] }>();
    (bidsJson["bid-multiplier"] as any[]).forEach((record, index) => {
      const raw = String(record[regionColumn] ?? "").trim();
      const key = raw.toLowerCase();
      if (!key || !treatmentLocations.has(key)) return;
      const bidValue = parseFloat(record["Bid Value"]) || 0;
      const coverage = parseFloat(record["Coverage"]) || 0;
      const existing = groups.get(key);
      if (existing) {
        existing.bidWeighted += bidValue * coverage;
        existing.coverageSum += coverage;
        existing.indices.push(index);
      } else {
        groups.set(key, { display: raw, bidWeighted: bidValue * coverage, coverageSum: coverage, indices: [index] });
      }
    });
    return Array.from(groups.entries())
      .map(([key, g]) => ({
        key,
        market: g.display,
        bidMultiplier: g.coverageSum > 0 ? g.bidWeighted / g.coverageSum : 0,
        coverage: g.coverageSum,
        segmentIndices: g.indices,
      }))
      .sort((a, b) => b.coverage - a.coverage);
  }, [bidsJson, regionColumn, treatmentLocations]);

  // Shared by the column filter dropdowns and the "Select All" button - both
  // need to know what a cell actually displays (formatted, not the raw
  // number) so filter values and matching stay consistent.
  const getDisplayValue = (columnKey: string, row: TreatmentMarketRow): string => {
    if (columnKey === "market") return row.market;
    if (columnKey === "bidMultiplier") return row.bidMultiplier.toFixed(2);
    if (columnKey === "coverage") return `${row.coverage.toFixed(2)}%`;
    return "";
  };

  const getKeysMatchingFilters = (filters: Record<string, any>) => {
    const activeEntries = Object.entries(filters).filter(
      (entry): entry is [string, any[]] => Array.isArray(entry[1]) && entry[1].length > 0
    );
    const matchingRows =
      activeEntries.length === 0
        ? treatmentMarketRows
        : treatmentMarketRows.filter((row) =>
            activeEntries.every(([column, values]) =>
              values.some((value) => getDisplayValue(column, row) === String(value))
            )
          );
    return matchingRows.map((row) => row.key);
  };

  const columnsWithFilters = useMemo(() => {
    const buildFilters = (columnKey: string, numeric = false) => {
      const unique = Array.from(new Set(treatmentMarketRows.map((row) => getDisplayValue(columnKey, row))));
      return unique
        .map((value) => ({ text: value, value }))
        .sort((a, b) => (numeric ? parseFloat(a.value) - parseFloat(b.value) : a.text.localeCompare(b.text)));
    };
    const withFilterProps = (columnKey: string, numeric = false) => ({
      filters: buildFilters(columnKey, numeric),
      onFilter: (value: React.Key | boolean, row: TreatmentMarketRow) =>
        getDisplayValue(columnKey, row) === String(value),
      filteredValue: filterState[columnKey] ?? null,
    });
    return {
      market: withFilterProps("market"),
      bidMultiplier: withFilterProps("bidMultiplier", true),
      coverage: withFilterProps("coverage", true),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentMarketRows, filterState]);

  const handleTableChange = (_pagination: any, filters: Record<string, any>) => {
    setFilterState(filters);
  };

  const handleSelectAll = () => {
    setSelectedMarketKeys(getKeysMatchingFilters(filterState));
  };

  const handleResetAll = () => {
    setSelectedMarketKeys([]);
    setFilterState({});
    setTableKey((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let outputJson;
        let outputUnavailable = false;
        try {
          const outputResponse = await axiosInstance.get(`/api/bid-multiplier/output/${jobConfigId}`);
          outputJson = outputResponse.data.result;
          if (!outputJson) outputUnavailable = true;
        } catch (apiError) {
          outputUnavailable = true;
        }

        if (outputUnavailable) {
          setIsProcessing(true);
        } else if (typeof outputJson === "string") {
          try {
            setBidsJson(JSON.parse(outputJson));
          } catch (error) {
            setIsProcessing(true);
          }
        } else if (outputJson && typeof outputJson === "object") {
          if (!outputJson["bid-multiplier"] || !Array.isArray(outputJson["bid-multiplier"]) || outputJson["bid-multiplier"].length === 0) {
            message.error("No bid multiplier data available");
          } else {
            setBidsJson(outputJson);
          }
        } else {
          setIsProcessing(true);
        }

        try {
          const configResponse = await axiosInstance.get(`/api/jobConfig/${jobConfigId}`);
          setJobConfigData(configResponse.data);
          setAppliedBids(configResponse.data?.modelOutputConfig?.outputData?.appliedBids || []);
        } catch (configError) {
          // No fallback shape needed here (unlike causal-loop's StepApplyBid)
          // - without a real jobConfigData, regionColumn stays undefined and
          // treatmentMarketRows resolves to an empty, clearly-labeled table
          // rather than a wrong guess.
        }

        try {
          const design = await fetchDesignResult(jobConfigId);
          const preview = design?.splitPreview?.["5050"];
          if (preview) {
            setTreatmentLocations(
              new Set(preview.treatmentLocations.map((m) => m.trim().toLowerCase()))
            );
          } else {
            setRctUnavailable(true);
          }
        } catch (err) {
          setRctUnavailable(true);
        }
      } catch (error) {
        setIsProcessing(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobConfigId]);

  const getRegionKey = (region: any, provider: "google" | "facebook") => {
    const regionItem = DMA_LIST.find((item) => item.name === region);
    return provider === "facebook" ? regionItem?.key : regionItem?.gg_ads_key;
  };

  const getLocaleKey = (locale: any, provider: "google" | "facebook") => {
    const localeItem = LOCALE_LIST.find((item) => item.name === locale);
    return provider === "facebook" ? localeItem?.key : localeItem?.gg_locale;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedSegmentIndices = useMemo(() => {
    const selected = new Set(selectedMarketKeys);
    return treatmentMarketRows
      .filter((r) => selected.has(r.key))
      .flatMap((r) => r.segmentIndices);
  }, [treatmentMarketRows, selectedMarketKeys]);

  const persistAppliedBid = async (
    provider: "google" | "meta" | "universal_ads",
    testCampaignId: string,
    testBudget: number
  ) => {
    try {
      const response = await axiosInstance.post(`/api/causal-loop/${jobConfigId}/apply-bids`, {
        provider,
        controlCampaignId: "",
        controlBudget: 0,
        testCampaignId,
        testBudget,
        selectedRowIndices: selectedSegmentIndices,
      });
      setAppliedBids((prev) => [...prev, response.data]);
    } catch (err) {
      console.error("Failed to save applied bids record:", err);
    }
  };

  const buildApplyPayload = (provider: "facebook" | "google") => {
    const columnMapping = jobConfigData?.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping || {};
    const selectedBidData = (bidsJson?.["bid-multiplier"] || []).filter((_: unknown, index: number) =>
      selectedSegmentIndices.includes(index)
    );
    return selectedBidData.map((item: any) => ({
      age: columnMapping?.age_varialbes?.length ? item[columnMapping.age_varialbes[0]] : undefined,
      devicePlatform: columnMapping?.device_platform_varialbes?.length
        ? item[columnMapping.device_platform_varialbes[0]]
        : undefined,
      gender: columnMapping?.gender_varialbes?.length ? item[columnMapping.gender_varialbes[0]] : undefined,
      locale: columnMapping?.locale_varialbes?.length
        ? getLocaleKey(item[columnMapping.locale_varialbes[0]], provider)
        : undefined,
      regions: columnMapping?.region_varialbes?.length
        ? getRegionKey(item[columnMapping.region_varialbes[0]], provider)
        : undefined,
      salesScaled: item["Bid Value"],
      userDevice: columnMapping?.user_devices_varialbes?.length
        ? item[columnMapping.user_devices_varialbes[0]]
        : undefined,
      userOS: columnMapping?.user_os_varialbes?.length ? item[columnMapping.user_os_varialbes[0]] : undefined,
    }));
  };

  const handleAlert = () => {
    setAlert(true);
    setTimeout(() => setAlert(false), 3000);
  };

  const onApplyBids = async () => {
    if (!formData.testAdsetId || !formData.testBudget) {
      message.error("Please fill in all fields");
      return;
    }
    if (selectedMarketKeys.length === 0) {
      message.error("Please select at least one Treatment market to apply bids for");
      return;
    }
    const payload = {
      jobConfigId,
      provider: "facebookads",
      testAdsetId: formData.testAdsetId,
      testBudget: parseFloat(formData.testBudget),
      payload: buildApplyPayload("facebook"),
    };
    setFacebookLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } finally {
      await persistAppliedBid("meta", formData.testAdsetId, parseFloat(formData.testBudget));
      setFacebookLoading(false);
    }
  };

  const onGoogleApplyBids = async () => {
    if (!formData.googleTestCampaignId || !formData.googleTestBudget) {
      message.error("Please fill in all fields");
      return;
    }
    if (selectedMarketKeys.length === 0) {
      message.error("Please select at least one Treatment market to apply bids for");
      return;
    }
    const payload = {
      jobConfigId,
      provider: "googleads",
      testCampaignId: formData.googleTestCampaignId,
      testBudget: parseFloat(formData.googleTestBudget),
      payload: buildApplyPayload("google"),
    };
    setGoogleLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Google");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Google");
      handleAlert();
    } finally {
      await persistAppliedBid("google", formData.googleTestCampaignId, parseFloat(formData.googleTestBudget));
      setGoogleLoading(false);
    }
  };

  const onUniversalApplyBids = async () => {
    if (!formData.universalTestCampaignId || !formData.universalTestBudget) {
      message.error("Please fill in all fields");
      return;
    }
    if (selectedMarketKeys.length === 0) {
      message.error("Please select at least one Treatment market to apply bids for");
      return;
    }
    const payload = {
      jobConfigId,
      provider: "universal_ads",
      testCampaignId: formData.universalTestCampaignId,
      testBudget: parseFloat(formData.universalTestBudget),
      payload: buildApplyPayload("google"),
    };
    setUniversalLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Universal Ads");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Universal Ads");
      handleAlert();
    } finally {
      await persistAppliedBid(
        "universal_ads",
        formData.universalTestCampaignId,
        parseFloat(formData.universalTestBudget)
      );
      setUniversalLoading(false);
    }
  };

  // Single Excel download, matching Step 3 (Power Analysis)'s Market Level
  // Investments download pattern exactly - one plain Button, no format
  // choice.
  const handleDownloadXlsx = () => {
    const rows = treatmentMarketRows.map((r) => ({
      Market: r.market,
      "Bid Multiplier": Number(r.bidMultiplier.toFixed(2)),
      "Coverage (%)": Number(r.coverage.toFixed(2)),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Treatment Markets");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "treatment_markets.xlsx"
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-full py-16">
        <Spin size="large" />
        <div className="mt-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Bid Optimizer Results</h3>
          <p className="text-sm text-gray-600">Please wait while we fetch your optimization results...</p>
        </div>
        <div className="mt-8 w-1/2">
          <Progress percent={100} status="active" strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }} />
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex flex-col space-y-6">
        <Card className="shadow-sm">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-6 flex items-center justify-center w-20 h-20 rounded-full bg-blue-50">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Test Running in Background</h3>
            <p className="text-sm text-gray-600 max-w-md mb-2">
              Your bid optimization test has been submitted and is still processing. This usually takes a
              few minutes.
            </p>
            <p className="text-sm text-gray-400">
              Click <strong>Previous</strong> and come back shortly, or check the Causal Loop (RCT) list
              once the job completes.
            </p>
          </div>
        </Card>
        <div className="flex justify-between">
          <Button size="large" onClick={onPrevious}>
            Previous
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <Card className="shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Treatment Markets</h3>
        <p className="text-sm text-gray-600 mb-6">
          The markets the RCT 50/50 design assigned to Treatment, with each market's Bid Optimizer
          multiplier and share of total sales (Coverage). Apply bids to the Treatment markets below.
        </p>

        {rctUnavailable && (
          <Alert
            className="mb-4"
            type="warning"
            showIcon
            message="RCT design results unavailable"
            description="The RCT 50/50 design didn't complete for this study, so Treatment markets can't be shown. You can still apply bids using the full Bid Optimizer output from the job list."
          />
        )}

        <Card className="mb-6">
          <div className="mb-4 flex justify-between items-center">
            <span className="text-sm font-medium text-gray-800">
              Selected Markets: {selectedMarketKeys.length} / {treatmentMarketRows.length}
            </span>
            <div className="flex items-center gap-2">
              <Button type="primary" onClick={handleSelectAll} disabled={treatmentMarketRows.length === 0}>
                Select All
              </Button>
              <Button onClick={handleResetAll} disabled={treatmentMarketRows.length === 0}>
                Reset All
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadXlsx}
                disabled={treatmentMarketRows.length === 0}
              >
                Download
              </Button>
            </div>
          </div>

          <Table
            key={tableKey}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selectedMarketKeys,
              onChange: setSelectedMarketKeys,
            }}
            columns={[
              { title: "Market", dataIndex: "market", key: "market", ...columnsWithFilters.market },
              {
                title: "Bid Multiplier",
                dataIndex: "bidMultiplier",
                key: "bidMultiplier",
                sorter: (a: TreatmentMarketRow, b: TreatmentMarketRow) => a.bidMultiplier - b.bidMultiplier,
                render: (v: number) => v.toFixed(2),
                ...columnsWithFilters.bidMultiplier,
              },
              {
                title: "Coverage",
                dataIndex: "coverage",
                key: "coverage",
                sorter: (a: TreatmentMarketRow, b: TreatmentMarketRow) => a.coverage - b.coverage,
                render: (v: number) => `${v.toFixed(2)}%`,
                ...columnsWithFilters.coverage,
              },
            ]}
            dataSource={treatmentMarketRows}
            rowKey="key"
            onChange={handleTableChange}
            pagination={{ pageSize: 50 }}
            bordered
            locale={{ emptyText: rctUnavailable ? "No RCT design results available" : "No Treatment markets found" }}
          />
        </Card>

        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" className="mb-6">
          <TabPane tab="Facebook" key="facebook">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">Facebook Bid Optimizer</h4>
              <Table
                columns={[
                  { title: "Adset Type", dataIndex: "type", key: "type" },
                  { title: "Adset ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Adset ID"
                        name="testAdsetId"
                        value={formData.testAdsetId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="testBudget"
                        value={formData.testBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onApplyBids} loading={facebookLoading}>
                  {facebookLoading ? "Applying Bids..." : "Apply Facebook Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Google" key="google">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">Google Bid Optimizer</h4>
              <Table
                columns={[
                  { title: "Campaign Type", dataIndex: "type", key: "type" },
                  { title: "Campaign ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Campaign ID"
                        name="googleTestCampaignId"
                        value={formData.googleTestCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="googleTestBudget"
                        value={formData.googleTestBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onGoogleApplyBids} loading={googleLoading}>
                  {googleLoading ? "Applying Bids..." : "Apply Google Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Universal Ads" key="universal_ads">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">Universal Ads Bid Optimizer</h4>
              <Table
                columns={[
                  { title: "Campaign Type", dataIndex: "type", key: "type" },
                  { title: "Campaign ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Campaign ID"
                        name="universalTestCampaignId"
                        value={formData.universalTestCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="universalTestBudget"
                        value={formData.universalTestBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onUniversalApplyBids} loading={universalLoading}>
                  {universalLoading ? "Applying Bids..." : "Apply Universal Ads Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>
        </Tabs>

        {appliedBids.length > 0 && (
          <div className="mt-2">
            <h4 className="text-base font-semibold text-gray-900 mb-3">Previously Applied</h4>
            <Table
              dataSource={appliedBids.map((bid, idx) => ({ ...bid, key: idx }))}
              pagination={false}
              bordered
              size="small"
              columns={[
                {
                  title: "Platform",
                  dataIndex: "provider",
                  key: "provider",
                  render: (p: string) => (
                    <Tag color={p === "google" ? "blue" : p === "meta" ? "geekblue" : "purple"}>
                      {PROVIDER_LABELS[p] || p}
                    </Tag>
                  ),
                },
                {
                  title: "Test Campaign",
                  key: "test",
                  render: (_: any, bid: any) => (
                    <div>
                      <div className="font-medium text-gray-800">{bid.testCampaignId}</div>
                      <div className="text-xs text-gray-500">${bid.testBudget}</div>
                    </div>
                  ),
                },
                {
                  title: "Rows Applied",
                  key: "rows",
                  align: "center" as const,
                  render: (_: any, bid: any) => bid.selectedRowIndices?.length ?? "-",
                },
                {
                  title: "Applied At",
                  dataIndex: "appliedAt",
                  key: "appliedAt",
                  render: (v: string) => (v ? new Date(v).toLocaleString(undefined, { timeZoneName: "short" }) : "-"),
                },
              ]}
            />
          </div>
        )}
      </Card>

      {isAlert && (
        <Alert
          message="Bid Applied Successfully"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          className="fixed bottom-8 right-8"
          closable
        />
      )}

      <div className="flex justify-between">
        <Button size="large" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="primary" size="large" onClick={onNext}>
          Finish
        </Button>
      </div>
    </div>
  );
};

export default StepApplyBid;
